/**
 * Customer Experience (CX): formulários de pesquisa com o cliente (NPS, CSAT, CES e perguntas livres, como num Google
 * Forms), escalas e os cálculos usados em todos os painéis.
 *
 * - NPS (Net Promoter Score; no projeto, pNPS): "De 0 a 10, quanto você recomendaria…". Promotores 9–10, neutros 7–8,
 *   detratores 0–6. NPS = % promotores − % detratores (de −100 a +100). Sempre com a pergunta aberta "por quê?".
 * - CSAT (Customer Satisfaction Score): satisfação de 1 a 5. CSAT = % de 4 e 5.
 * - CES (Customer Effort Score): "A AD Digital tornou fácil…", de 1 (discordo totalmente) a 7 (concordo totalmente).
 *   CES = média; também a % de "fácil" (5 a 7).
 */
import { z } from 'zod';

export const CX_QUESTION_TYPES = ['nps', 'csat', 'ces', 'text', 'long_text', 'single', 'multiple', 'yes_no', 'rating'] as const;
export type CxQuestionType = (typeof CX_QUESTION_TYPES)[number];
export const CX_QUESTION_TYPE_LABEL: Record<CxQuestionType, string> = {
  nps: 'NPS (0 a 10)', csat: 'CSAT · satisfação (1 a 5)', ces: 'CES · esforço (1 a 7)', text: 'Resposta curta', long_text: 'Parágrafo (comentário)',
  single: 'Múltipla escolha (uma opção)', multiple: 'Caixas de seleção (várias opções)', yes_no: 'Sim ou não', rating: 'Nota de 1 a 5 (estrelas)',
};
/** Tipos que viram indicador (só um de cada por formulário). */
export const CX_METRIC_TYPES = ['nps', 'csat', 'ces'] as const;
export type CxMetric = (typeof CX_METRIC_TYPES)[number];

export const CSAT_LABELS = ['Muito insatisfeito', 'Insatisfeito', 'Neutro', 'Satisfeito', 'Muito satisfeito'] as const;
export const CES_LABELS = ['Discordo totalmente', 'Discordo', 'Discordo em parte', 'Neutro', 'Concordo em parte', 'Concordo', 'Concordo totalmente'] as const;
export const CX_SOURCES = ['link', 'manual', 'importacao'] as const;
export type CxSource = (typeof CX_SOURCES)[number];
export const CX_SOURCE_LABEL: Record<CxSource, string> = { link: 'Link da pesquisa', manual: 'Incluída à mão', importacao: 'Importada' };
export const CX_SURVEY_STATUSES = ['pendente', 'enviado', 'respondido', 'sem_email', 'expirado', 'cancelado'] as const;
export type CxSurveyStatus = (typeof CX_SURVEY_STATUSES)[number];
export const CX_SURVEY_STATUS_LABEL: Record<CxSurveyStatus, string> = {
  pendente: 'Link criado (não enviado)', enviado: 'Enviada', respondido: 'Respondida', sem_email: 'Sem e-mail do cliente', expirado: 'Expirada', cancelado: 'Cancelada',
};
export const CX_TRIGGERS = ['manual', 'fim_projeto', 'marco'] as const;
export type CxTrigger = (typeof CX_TRIGGERS)[number];
export const CX_TRIGGER_LABEL: Record<CxTrigger, string> = { manual: 'Envio manual', fim_projeto: 'Fim do projeto', marco: 'Marco do cronograma' };

const qid = z.string().regex(/^[a-z0-9_-]{1,40}$/i);
export const CxQuestion = z.object({
  id: qid,
  type: z.enum(CX_QUESTION_TYPES),
  label: z.string().trim().min(3, 'Escreva a pergunta').max(500),
  help: z.string().trim().max(500).optional(),
  required: z.boolean().default(false),
  options: z.array(z.string().trim().min(1).max(200)).max(30).optional(),
});
export type CxQuestion = z.infer<typeof CxQuestion>;

export const CxFormInput = z.object({
  name: z.string().trim().min(3).max(150),
  description: z.string().trim().max(1000).nullable().optional(),
  intro: z.string().trim().max(2000).nullable().optional(),
  thanks: z.string().trim().max(2000).nullable().optional(),
  anonymous: z.boolean().default(false),
  active: z.boolean().default(true),
  isDefault: z.boolean().optional(),
  questions: z.array(CxQuestion).min(1, 'Inclua ao menos uma pergunta').max(40),
}).superRefine((f, ctx) => {
  const ids = new Set<string>();
  for (const [i, q] of f.questions.entries()) {
    if (ids.has(q.id)) ctx.addIssue({ code: 'custom', path: ['questions', i, 'id'], message: 'Pergunta repetida' });
    ids.add(q.id);
    if ((q.type === 'single' || q.type === 'multiple') && (q.options?.length ?? 0) < 2) ctx.addIssue({ code: 'custom', path: ['questions', i, 'options'], message: `"${q.label}": informe ao menos duas opções` });
  }
  for (const m of CX_METRIC_TYPES) {
    if (f.questions.filter((q) => q.type === m).length > 1) ctx.addIssue({ code: 'custom', path: ['questions'], message: `Só uma pergunta ${m.toUpperCase()} por formulário` });
  }
});
export type CxFormInput = z.infer<typeof CxFormInput>;

/** Formulário padrão: NPS com o "por quê?", CSAT, CES e sugestões (o CSM ajusta em Customer Experience › Formulários). */
export const DEFAULT_CX_FORM: CxFormInput = {
  name: 'Pesquisa de satisfação do projeto',
  description: 'Enviada ao cliente no fim do projeto (e, se ligado, nos marcos).',
  intro: 'Sua opinião ajuda a AD Digital a melhorar a forma como conduzimos os projetos. Leva cerca de 2 minutos.',
  thanks: 'Obrigado! Sua resposta foi registrada e será lida pela equipe de Customer Success da AD Digital.',
  anonymous: false, active: true, isDefault: true,
  questions: [
    { id: 'nps', type: 'nps', label: 'Em uma escala de 0 a 10, quanto você recomendaria este projeto, a equipe e a AD Digital a um colega?', required: true },
    { id: 'nps_motivo', type: 'long_text', label: 'O que mais pesou na sua nota?', required: false },
    { id: 'csat', type: 'csat', label: 'De forma geral, qual é a sua satisfação com o projeto?', required: true },
    { id: 'ces', type: 'ces', label: 'A AD Digital tornou fácil para você resolver o que precisava neste projeto.', required: true },
    { id: 'sugestoes', type: 'long_text', label: 'Tem alguma sugestão ou comentário para a equipe?', required: false },
  ],
};

// ------------------------------------------------------------ cálculos
export interface NpsStats { n: number; promoters: number; passives: number; detractors: number; nps: number | null; promotersPct: number; passivesPct: number; detractorsPct: number;
  /** Margem de erro (95%) em pontos de NPS; null sem respostas. */ margin: number | null }
export function npsCategory(score: number): 'promotor' | 'neutro' | 'detrator' { return score >= 9 ? 'promotor' : score >= 7 ? 'neutro' : 'detrator'; }
export function npsStats(scores: number[]): NpsStats {
  const n = scores.length;
  const promoters = scores.filter((s) => s >= 9).length;
  const detractors = scores.filter((s) => s <= 6).length;
  const passives = n - promoters - detractors;
  if (!n) return { n, promoters, passives, detractors, nps: null, promotersPct: 0, passivesPct: 0, detractorsPct: 0, margin: null };
  const p = promoters / n; const d = detractors / n;
  const variance = p + d - (p - d) ** 2;
  return { n, promoters, passives, detractors, nps: Math.round((p - d) * 100), promotersPct: Math.round(p * 1000) / 10, passivesPct: Math.round((passives / n) * 1000) / 10,
    detractorsPct: Math.round(d * 1000) / 10, margin: Math.round(1.96 * Math.sqrt(variance / n) * 100) };
}
export interface CsatStats { n: number; csat: number | null; avg: number | null; dist: number[] }
export function csatStats(scores: number[]): CsatStats {
  const n = scores.length;
  const dist = [1, 2, 3, 4, 5].map((v) => scores.filter((s) => s === v).length);
  return { n, csat: n ? Math.round(((dist[3]! + dist[4]!) / n) * 1000) / 10 : null, avg: n ? Math.round((scores.reduce((a, b) => a + b, 0) / n) * 100) / 100 : null, dist };
}
export interface CesStats { n: number; avg: number | null; easyPct: number | null; dist: number[] }
export function cesStats(scores: number[]): CesStats {
  const n = scores.length;
  const dist = [1, 2, 3, 4, 5, 6, 7].map((v) => scores.filter((s) => s === v).length);
  return { n, avg: n ? Math.round((scores.reduce((a, b) => a + b, 0) / n) * 100) / 100 : null, easyPct: n ? Math.round((scores.filter((s) => s >= 5).length / n) * 1000) / 10 : null, dist };
}
/** Faixa de leitura do NPS (referência de mercado). */
export function npsZone(nps: number | null): { label: string; tone: 'err' | 'warn' | 'ok' | 'muted' } {
  if (nps == null) return { label: 'sem respostas', tone: 'muted' };
  if (nps < 0) return { label: 'zona crítica', tone: 'err' };
  if (nps < 50) return { label: 'zona de aperfeiçoamento', tone: 'warn' };
  if (nps < 75) return { label: 'zona de qualidade', tone: 'ok' };
  return { label: 'zona de excelência', tone: 'ok' };
}

/** Confere as respostas contra as perguntas e devolve o que fica guardado (e os indicadores). Erros em português. */
export function validateAnswers(questions: CxQuestion[], raw: Record<string, unknown>): { answers: Record<string, string | number | string[] | boolean>; errors: string[];
  nps: number | null; csat: number | null; ces: number | null; comment: string | null } {
  const answers: Record<string, string | number | string[] | boolean> = {};
  const errors: string[] = [];
  const int = (v: unknown) => (v === '' || v == null ? null : Number.isInteger(Number(v)) ? Number(v) : NaN);
  for (const q of questions) {
    const v = raw[q.id];
    const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) { if (q.required) errors.push(`Responda: ${q.label}`); continue; }
    switch (q.type) {
      case 'nps': case 'csat': case 'ces': case 'rating': {
        const [lo, hi] = q.type === 'nps' ? [0, 10] : q.type === 'ces' ? [1, 7] : [1, 5];
        const n = int(v);
        if (n == null || Number.isNaN(n) || n < lo || n > hi) errors.push(`${q.label}: escolha de ${lo} a ${hi}`); else answers[q.id] = n;
        break;
      }
      case 'yes_no': answers[q.id] = v === true || v === 'sim' || v === 'true'; break;
      case 'single': {
        const s = String(v);
        if (!q.options?.includes(s)) errors.push(`${q.label}: opção inválida`); else answers[q.id] = s;
        break;
      }
      case 'multiple': {
        const list = (Array.isArray(v) ? v : [v]).map(String);
        if (list.some((s) => !q.options?.includes(s))) errors.push(`${q.label}: opção inválida`); else answers[q.id] = list;
        break;
      }
      default: answers[q.id] = String(v).trim().slice(0, q.type === 'text' ? 500 : 5000);
    }
  }
  const metric = (t: CxMetric) => { const q = questions.find((x) => x.type === t); const a = q ? answers[q.id] : undefined; return typeof a === 'number' ? a : null; };
  const comment = questions.filter((q) => q.type === 'long_text' || q.type === 'text').map((q) => answers[q.id]).filter((a): a is string => typeof a === 'string' && !!a.trim()).join('\n\n') || null;
  return { answers, errors, nps: metric('nps'), csat: metric('csat'), ces: metric('ces'), comment };
}

export const CxAutoSettings = z.object({
  enabled: z.boolean(),
  /** Quando o projeto chega nesta fase, a pesquisa vai para o sponsor. */
  phase: z.enum(['finalizado', 'encerramento']).default('finalizado'),
  milestones: z.boolean().default(false),
  formId: z.number().int().positive().nullable().default(null),
  reminderDays: z.number().int().min(0).max(60).default(7),
  maxReminders: z.number().int().min(0).max(5).default(1),
  expiresDays: z.number().int().min(7).max(180).default(30),
  /** Desde quando vale (só projetos que mudam de fase depois disso; os antigos não recebem). */
  since: z.string().nullable().default(null),
});
export type CxAutoSettings = z.infer<typeof CxAutoSettings>;
